import React, { useState, useEffect } from 'react';
import { 
  Receipt, Calculator, CheckCircle, Clock, FileText, 
  ChevronDown, ArrowRight, Shield, Award, Scale,
  DollarSign, Calendar, Zap, Phone, Building2,
  Percent, TrendingDown, BadgeCheck, BookOpen, AlertCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import FloatingButtons from '../components/FloatingButtons';

const BeneficiosFiscalesPage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const taxBenefits = [
    {
      icon: Percent,
      title: 'Exoneración del IVA',
      description: 'Los equipos e insumos para energía renovable están exentos del 10% de IVA en importación y compra local.',
      savings: 'Ahorro del 10%',
      applicable: 'Paneles, inversores, baterías, estructuras',
      law: 'Ley 5804/2017'
    },
    {
      icon: TrendingDown,
      title: 'Exoneración Arancelaria',
      description: 'Importación de equipos solares con 0% de arancel aduanero.',
      savings: 'Ahorro del 6-12%',
      applicable: 'Equipos importados',
      law: 'Ley 5804/2017'
    },
    {
      icon: Receipt,
      title: 'Deducción del IRACIS',
      description: 'Las inversiones en energía renovable son deducibles del Impuesto a la Renta Comercial.',
      savings: 'Hasta 100% deducible',
      applicable: 'Empresas contribuyentes',
      law: 'Ley 5804/2017'
    },
    {
      icon: Building2,
      title: 'Amortización Acelerada',
      description: 'Depreciación acelerada de equipos en 5 años en lugar del estándar de 10-15 años.',
      savings: 'Beneficio fiscal anticipado',
      applicable: 'Activos fijos',
      law: 'Decreto reglamentario'
    }
  ];

  const laws = [
    {
      name: 'Ley 5804/2017',
      title: 'De Fomento de las Energías Renovables',
      description: 'Marco legal principal que establece incentivos fiscales para proyectos de energía renovable en Paraguay.',
      benefits: [
        'Exoneración de IVA en equipos',
        'Exoneración de aranceles de importación',
        'Deducibilidad del IRACIS',
        'Amortización acelerada'
      ]
    },
    {
      name: 'Ley 3009/2006',
      title: 'De Producción y Transporte de Energía Eléctrica',
      description: 'Regula la generación distribuida y permite a los usuarios inyectar excedentes a la red.',
      benefits: [
        'Net metering (medición neta)',
        'Compensación por excedentes',
        'Prioridad de despacho'
      ]
    },
    {
      name: 'Decreto 7406/2017',
      title: 'Reglamentación de Energías Renovables',
      description: 'Establece los procedimientos y requisitos para acceder a los beneficios de la Ley 5804.',
      benefits: [
        'Requisitos de registro',
        'Procedimientos de certificación',
        'Auditorías y controles'
      ]
    }
  ];

  const savingsExample = {
    systemSize: '10 kW',
    totalCost: 'USD 12,000',
    breakdown: [
      { concept: 'IVA (10%)', without: 'USD 1,200', with: 'USD 0', saving: 'USD 1,200' },
      { concept: 'Arancel (8%)', without: 'USD 960', with: 'USD 0', saving: 'USD 960' },
      { concept: 'IRACIS (10%)', without: 'N/A', with: 'Deducible', saving: '~USD 1,200' }
    ],
    totalSaving: 'USD 3,360',
    savingPercent: '28%'
  };

  const requirements = [
    {
      title: 'Registro en SET',
      description: 'Estar inscripto en el Registro Único de Contribuyentes de la SET.',
      icon: FileText
    },
    {
      title: 'Certificación del Proyecto',
      description: 'El proyecto debe ser certificado como "de energía renovable" por el VMME.',
      icon: BadgeCheck
    },
    {
      title: 'Facturación Legal',
      description: 'Las compras deben estar respaldadas por facturas legales de proveedores autorizados.',
      icon: Receipt
    },
    {
      title: 'Uso Declarado',
      description: 'Los equipos deben ser declarados para uso en generación de energía renovable.',
      icon: BookOpen
    }
  ];

  const faqs = [
    {
      question: '¿Cómo accedo a la exoneración del IVA?',
      answer: 'Al comprar con Zenex, nos encargamos de tramitar la exoneración. Emitimos factura con IVA 0% para los equipos que califican según la Ley 5804. El proceso es automático y no tiene costo adicional.'
    },
    {
      question: '¿Qué equipos están exentos de impuestos?',
      answer: 'Están exentos: paneles solares, inversores, baterías, estructuras de montaje, cables especiales para sistemas fotovoltaicos, medidores bidireccionales y otros componentes directamente relacionados con la generación solar.'
    },
    {
      question: '¿Puedo deducir la inversión de mi IRACIS?',
      answer: 'Sí, las empresas pueden deducir el 100% de la inversión en energía renovable de su Impuesto a la Renta. Es importante conservar toda la documentación y que el proyecto esté debidamente certificado.'
    },
    {
      question: '¿Los beneficios aplican para sistemas residenciales?',
      answer: 'La exoneración de IVA y aranceles aplica tanto para residencias como empresas. La deducción del IRACIS es exclusiva para contribuyentes de este impuesto (empresas).'
    },
    {
      question: '¿Hay un límite de potencia para acceder a beneficios?',
      answer: 'No hay límite de potencia para la exoneración de IVA y aranceles. Para la generación distribuida conectada a red, el límite actual es de 500 kW para autoconsumo.'
    },
    {
      question: '¿Zenex gestiona todos los trámites fiscales?',
      answer: 'Sí, nos encargamos de la gestión completa: registro del proyecto, tramitación de exoneraciones, facturación correcta y documentación para tu contador. Todo incluido en nuestro servicio.'
    }
  ];

  const processSteps = [
    {
      step: 1,
      title: 'Presupuesto con Beneficios',
      description: 'Te presentamos el presupuesto mostrando claramente los impuestos exonerados y el ahorro real.'
    },
    {
      step: 2,
      title: 'Registro del Proyecto',
      description: 'Registramos tu proyecto en el VMME para certificar que califica como energía renovable.'
    },
    {
      step: 3,
      title: 'Compra con Exoneración',
      description: 'Importamos o adquirimos los equipos con las exoneraciones aplicadas. Facturamos con IVA 0%.'
    },
    {
      step: 4,
      title: 'Documentación Fiscal',
      description: 'Te entregamos toda la documentación necesaria para tu declaración de impuestos y auditorías.'
    }
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="beneficios-fiscales-page">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-20 pb-32 bg-gradient-to-br from-amber-900 via-orange-800 to-red-900 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-20 w-72 h-72 bg-amber-500/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-orange-400/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex items-center gap-2 text-amber-400 mb-6">
            <Receipt className="w-5 h-5" />
            <span className="text-sm font-medium tracking-wider uppercase">Incentivos Fiscales</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Beneficios Fiscales
            <span className="block text-amber-400">Ahorrá Hasta 28% en Impuestos</span>
          </h1>
          
          <p className="text-xl text-zinc-300 max-w-2xl mb-8">
            Paraguay ofrece importantes exenciones fiscales para proyectos de energía solar. 
            Conocé cómo maximizar tu inversión aprovechando los beneficios de la ley.
          </p>
          
          <div className="flex flex-wrap gap-4">
            <button 
              onClick={() => navigate('/calculadora')}
              className="px-8 py-4 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-xl transition-all duration-300 flex items-center gap-2 shadow-lg shadow-amber-500/30"
            >
              Calcular Mis Beneficios
              <Calculator className="w-5 h-5" />
            </button>
            <a 
              href="https://wa.me/595991344956?text=Hola,%20quiero%20información%20sobre%20beneficios%20fiscales%20para%20energía%20solar"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-xl transition-all duration-300 flex items-center gap-2 backdrop-blur-sm"
            >
              <Phone className="w-5 h-5" />
              Asesoría Fiscal
            </a>
          </div>
          
          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16">
            {[
              { value: '0%', label: 'IVA en equipos' },
              { value: '0%', label: 'Arancel importación' },
              { value: '100%', label: 'Deducible IRACIS' },
              { value: '28%', label: 'Ahorro total' }
            ].map((stat, index) => (
              <div key={index} className="text-center p-4 bg-white/5 rounded-xl backdrop-blur-sm">
                <p className="text-3xl font-bold text-amber-400">{stat.value}</p>
                <p className="text-sm text-zinc-400">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tax Benefits Section */}
      <section className="py-20 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              Beneficios Tributarios Disponibles
            </h2>
            <p className="text-lg text-zinc-600 max-w-2xl mx-auto">
              La Ley 5804/2017 establece múltiples incentivos para proyectos de energía renovable.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {taxBenefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div 
                  key={index} 
                  className="p-8 bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 border border-zinc-100"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Icon className="w-7 h-7 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-zinc-900 mb-2">{benefit.title}</h3>
                      <p className="text-zinc-600 mb-4">{benefit.description}</p>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="px-3 py-1 bg-emerald-100 text-emerald-700 text-sm font-medium rounded-full">
                            {benefit.savings}
                          </span>
                        </div>
                        <p className="text-sm text-zinc-500">
                          <strong>Aplica a:</strong> {benefit.applicable}
                        </p>
                        <p className="text-sm text-amber-600 font-medium">
                          Base legal: {benefit.law}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Savings Example */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              Ejemplo de Ahorro Real
            </h2>
            <p className="text-lg text-zinc-600">
              Así se ve el ahorro fiscal en un sistema de {savingsExample.systemSize}
            </p>
          </div>

          <div className="bg-gradient-to-br from-zinc-900 to-zinc-800 rounded-2xl p-8 text-white">
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              <div className="text-center">
                <p className="text-zinc-400 mb-1">Tamaño del sistema</p>
                <p className="text-2xl font-bold text-amber-400">{savingsExample.systemSize}</p>
              </div>
              <div className="text-center">
                <p className="text-zinc-400 mb-1">Costo base</p>
                <p className="text-2xl font-bold">{savingsExample.totalCost}</p>
              </div>
              <div className="text-center">
                <p className="text-zinc-400 mb-1">Ahorro total</p>
                <p className="text-2xl font-bold text-emerald-400">{savingsExample.totalSaving}</p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-700">
                    <th className="text-left py-3 px-4 text-zinc-400 font-medium">Concepto</th>
                    <th className="text-center py-3 px-4 text-zinc-400 font-medium">Sin beneficios</th>
                    <th className="text-center py-3 px-4 text-zinc-400 font-medium">Con Zenex</th>
                    <th className="text-center py-3 px-4 text-zinc-400 font-medium">Ahorro</th>
                  </tr>
                </thead>
                <tbody>
                  {savingsExample.breakdown.map((row, index) => (
                    <tr key={index} className="border-b border-zinc-800">
                      <td className="py-3 px-4 text-zinc-300">{row.concept}</td>
                      <td className="py-3 px-4 text-center text-red-400">{row.without}</td>
                      <td className="py-3 px-4 text-center text-emerald-400">{row.with}</td>
                      <td className="py-3 px-4 text-center text-amber-400 font-medium">{row.saving}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-zinc-700">
                    <td className="py-4 px-4 font-bold">TOTAL AHORRO</td>
                    <td></td>
                    <td></td>
                    <td className="py-4 px-4 text-center text-emerald-400 font-bold text-xl">
                      {savingsExample.totalSaving} ({savingsExample.savingPercent})
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Legal Framework */}
      <section className="py-20 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              Marco Legal
            </h2>
            <p className="text-lg text-zinc-600 max-w-2xl mx-auto">
              Conocé las leyes que respaldan los beneficios fiscales para energía renovable en Paraguay.
            </p>
          </div>

          <div className="space-y-6">
            {laws.map((law, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-zinc-100">
                <div className="flex flex-col md:flex-row md:items-start gap-6">
                  <div className="md:w-1/3">
                    <div className="flex items-center gap-2 mb-2">
                      <Scale className="w-5 h-5 text-amber-600" />
                      <span className="font-bold text-amber-600">{law.name}</span>
                    </div>
                    <h3 className="text-xl font-bold text-zinc-900">{law.title}</h3>
                  </div>
                  <div className="md:w-2/3">
                    <p className="text-zinc-600 mb-4">{law.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {law.benefits.map((benefit, idx) => (
                        <span 
                          key={idx}
                          className="px-3 py-1 bg-amber-100 text-amber-700 text-sm rounded-full"
                        >
                          {benefit}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              Requisitos para Acceder
            </h2>
            <p className="text-lg text-zinc-600 max-w-2xl mx-auto">
              Estos son los requisitos básicos para aprovechar los beneficios fiscales.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {requirements.map((req, index) => {
              const Icon = req.icon;
              return (
                <div key={index} className="p-6 bg-zinc-50 rounded-xl text-center">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-6 h-6 text-amber-600" />
                  </div>
                  <h3 className="font-bold text-zinc-900 mb-2">{req.title}</h3>
                  <p className="text-sm text-zinc-600">{req.description}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-12 p-6 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-4">
            <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-bold text-amber-800 mb-1">Importante</h4>
              <p className="text-amber-700 text-sm">
                Zenex se encarga de todos los trámites y certificaciones necesarias. Nuestro servicio incluye 
                la gestión completa de las exoneraciones fiscales sin costo adicional.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-zinc-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              ¿Cómo Funciona con Zenex?
            </h2>
            <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
              Nosotros nos encargamos de todo. Vos solo disfrutás el ahorro.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <div key={index} className="text-center relative">
                {index < processSteps.length - 1 && (
                  <div className="hidden md:block absolute top-6 left-1/2 w-full h-0.5 bg-amber-600/30"></div>
                )}
                <div className="relative z-10 w-12 h-12 bg-amber-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold">
                  {step.step}
                </div>
                <h3 className="text-lg font-semibold mb-2">{step.title}</h3>
                <p className="text-zinc-400 text-sm">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-20 bg-zinc-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              Preguntas Frecuentes
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="border border-zinc-200 rounded-xl overflow-hidden bg-white"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-zinc-50 transition-colors"
                >
                  <span className="font-semibold text-zinc-900 pr-4">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-zinc-500 transition-transform ${openFaq === index ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === index && (
                  <div className="px-5 pb-5 text-zinc-600">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-amber-600 to-orange-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Receipt className="w-16 h-16 mx-auto text-white/80 mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Maximizá tu inversión
          </h2>
          <p className="text-xl text-amber-100 mb-8">
            No dejes dinero sobre la mesa. Aprovechá todos los beneficios fiscales disponibles.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="https://wa.me/595991344956?text=Hola,%20quiero%20asesoramiento%20sobre%20beneficios%20fiscales%20para%20energía%20solar"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 bg-white text-amber-600 font-semibold rounded-xl hover:bg-amber-50 transition-all flex items-center gap-2"
            >
              <Phone className="w-5 h-5" />
              Asesoría Fiscal Gratis
            </a>
            <button 
              onClick={() => navigate('/calculadora')}
              className="px-8 py-4 bg-amber-800/50 text-white font-semibold rounded-xl hover:bg-amber-800 transition-all flex items-center gap-2"
            >
              <Calculator className="w-5 h-5" />
              Calcular Ahorro
            </button>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default BeneficiosFiscalesPage;
